import React, { useRef, useMemo, useEffect } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';

// Shared geometries - created once, reused
const sharedBeamGeometry = new THREE.PlaneGeometry(1.5, 0.9);
const sharedGlowGeometry = new THREE.PlaneGeometry(3, 1.5);
const sharedFlashGeometry = new THREE.PlaneGeometry(12, 1.4);
const sharedCoreGeometry = new THREE.PlaneGeometry(11, 0.8);
const sharedOctaGeometry = new THREE.OctahedronGeometry(1, 0);
const sharedTrailGeometry = new THREE.PlaneGeometry(0.5, 3);
const sharedSphereGeometry = new THREE.SphereGeometry(0.15, 8, 8);
const sharedRingGeometry = new THREE.RingGeometry(0.8, 1.0, 32);
const sharedStreakGeometry = new THREE.PlaneGeometry(1, 0.06);

// Temp objects for matrix calculations
const tempMatrix = new THREE.Matrix4();
const tempPosition = new THREE.Vector3();
const tempScale = new THREE.Vector3();
const tempQuaternion = new THREE.Quaternion();
const tempEuler = new THREE.Euler();

const IceLineClearEffect = ({ rows, linesCleared, onComplete }) => {
  const groupRef = useRef();
  const startTime = useRef(null);

  // Refs for instanced meshes
  const beamInstanceRef = useRef();
  const beamGlowInstanceRef = useRef();
  const flashInstanceRef = useRef();
  const flashCoreInstanceRef = useRef();
  const shardInstanceRef = useRef();
  const shardTrailInstanceRef = useRef();
  const particleInstanceRef = useRef();
  const ringInstanceRef = useRef();
  const streakInstanceRef = useRef();

  // Colors
  const iceColors = useMemo(() => [
    new THREE.Color('#00FFFF'),
    new THREE.Color('#40E0D0'),
    new THREE.Color('#00CED1'),
    new THREE.Color('#80FFFF'),
    new THREE.Color('#FFFFFF'),
  ], []);

  // Generate crystal shards data
  const crystalShards = useMemo(() => {
    const shards = [];
    rows.forEach((row, rowIndex) => {
      const baseY = 20 - row - 0.5;
      const shardCount = 20;

      for (let i = 0; i < shardCount; i++) {
        const x = (i / shardCount) * 10 - 5 + (Math.random() - 0.5) * 0.8;
        const angle = (Math.random() - 0.5) * Math.PI * 0.8;
        const speed = 4 + Math.random() * 6;
        const upwardBias = 3 + Math.random() * 4;

        shards.push({
          startPosition: [x, baseY, 0.3],
          velocity: [
            Math.cos(angle) * speed + x * 0.5,
            upwardBias,
            (Math.random() - 0.5) * 2 + 1,
          ],
          rotationSpeed: [
            (Math.random() - 0.5) * 10,
            (Math.random() - 0.5) * 10,
            (Math.random() - 0.5) * 10,
          ],
          rotation: [0, 0, 0],
          size: 0.06 + Math.random() * 0.08,
          delay: rowIndex * 0.03 + Math.random() * 0.06,
          color: iceColors[Math.floor(Math.random() * iceColors.length)],
        });
      }
    });
    return shards;
  }, [rows, iceColors]);

  // Generate floating particles data
  const glowParticles = useMemo(() => {
    const particles = [];
    rows.forEach((row, rowIndex) => {
      const baseY = 20 - row - 0.5;
      for (let i = 0; i < 15; i++) {
        particles.push({
          startPosition: [
            (Math.random() - 0.5) * 10,
            baseY + (Math.random() - 0.5) * 0.3,
            0.3,
          ],
          delay: rowIndex * 0.02 + Math.random() * 0.1,
          speed: 2 + Math.random() * 2,
          drift: Math.random() * Math.PI * 2,
        });
      }
    });
    return particles;
  }, [rows]);

  // Generate energy streaks data
  const energyStreaks = useMemo(() => {
    const streaks = [];
    rows.forEach((row, i) => {
      for (let j = 0; j < 3; j++) {
        streaks.push({
          row,
          y: 20 - row - 0.5 + (j - 1) * 0.15,
          delay: i * 0.03 + j * 0.02,
          direction: j % 2 === 0 ? 1 : -1,
        });
      }
    });
    return streaks;
  }, [rows]);

  // Row data for sweeps, flashes, and rings
  const rowData = useMemo(() => {
    return rows.map((row, i) => ({
      row,
      y: 20 - row - 0.5,
      sweepDelay: i * 0.04,
      sweepDirection: i % 2 === 0 ? 1 : -1,
      flashDelay: i * 0.02 + 0.05,
      ringDelay: i * 0.03 + 0.08,
    }));
  }, [rows]);

  // Materials
  const beamMaterial = useMemo(() => new THREE.MeshBasicMaterial({
    color: 0xffffff,
    transparent: true,
    opacity: 1,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
  }), []);

  const beamGlowMaterial = useMemo(() => new THREE.MeshBasicMaterial({
    color: 0x00ffff,
    transparent: true,
    opacity: 0.6,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
  }), []);

  const flashMaterial = useMemo(() => new THREE.MeshBasicMaterial({
    color: 0x00e5e5,
    transparent: true,
    opacity: 0.8,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
  }), []);

  const flashCoreMaterial = useMemo(() => new THREE.MeshBasicMaterial({
    color: 0xffffff,
    transparent: true,
    opacity: 1,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
  }), []);

  const shardMaterial = useMemo(() => new THREE.MeshStandardMaterial({
    color: 0x00ffff,
    emissive: 0x00ffff,
    emissiveIntensity: 2,
    metalness: 0.9,
    roughness: 0.1,
    transparent: true,
    opacity: 0.9,
  }), []);

  const trailMaterial = useMemo(() => new THREE.MeshBasicMaterial({
    color: 0x00ffff,
    transparent: true,
    opacity: 0.4,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
  }), []);

  const particleMaterial = useMemo(() => new THREE.MeshBasicMaterial({
    color: 0x80ffff,
    transparent: true,
    opacity: 1,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
  }), []);

  const ringMaterial = useMemo(() => new THREE.MeshBasicMaterial({
    color: 0x00ffff,
    transparent: true,
    opacity: 0.7,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
    side: THREE.DoubleSide,
  }), []);

  const streakMaterial = useMemo(() => new THREE.MeshBasicMaterial({
    color: 0xffffff,
    transparent: true,
    opacity: 0.9,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
  }), []);

  // Initialize instances
  useEffect(() => {
    // Initialize beams
    if (beamInstanceRef.current) {
      rowData.forEach((data, i) => {
        tempPosition.set(-7, data.y, 0.5);
        tempScale.setScalar(0);
        tempMatrix.compose(tempPosition, tempQuaternion, tempScale);
        beamInstanceRef.current.setMatrixAt(i, tempMatrix);
      });
      beamInstanceRef.current.instanceMatrix.needsUpdate = true;
    }

    // Initialize beam glows
    if (beamGlowInstanceRef.current) {
      rowData.forEach((data, i) => {
        tempPosition.set(-7, data.y, 0.4);
        tempScale.setScalar(0);
        tempMatrix.compose(tempPosition, tempQuaternion, tempScale);
        beamGlowInstanceRef.current.setMatrixAt(i, tempMatrix);
      });
      beamGlowInstanceRef.current.instanceMatrix.needsUpdate = true;
    }

    // Initialize shards
    if (shardInstanceRef.current) {
      crystalShards.forEach((shard, i) => {
        tempPosition.set(...shard.startPosition);
        tempScale.setScalar(0);
        tempMatrix.compose(tempPosition, tempQuaternion, tempScale);
        shardInstanceRef.current.setMatrixAt(i, tempMatrix);
        shardInstanceRef.current.setColorAt(i, shard.color);
      });
      shardInstanceRef.current.instanceMatrix.needsUpdate = true;
      shardInstanceRef.current.instanceColor.needsUpdate = true;
    }

    // Initialize shard trails
    if (shardTrailInstanceRef.current) {
      crystalShards.forEach((_, i) => {
        tempScale.setScalar(0);
        tempMatrix.compose(tempPosition, tempQuaternion, tempScale);
        shardTrailInstanceRef.current.setMatrixAt(i, tempMatrix);
      });
      shardTrailInstanceRef.current.instanceMatrix.needsUpdate = true;
    }

    // Initialize particles
    if (particleInstanceRef.current) {
      glowParticles.forEach((particle, i) => {
        tempPosition.set(...particle.startPosition);
        tempScale.setScalar(0);
        tempMatrix.compose(tempPosition, tempQuaternion, tempScale);
        particleInstanceRef.current.setMatrixAt(i, tempMatrix);
      });
      particleInstanceRef.current.instanceMatrix.needsUpdate = true;
    }

    // Initialize rings
    if (ringInstanceRef.current) {
      rowData.forEach((data, i) => {
        tempPosition.set(0, data.y, 0.2);
        tempEuler.set(Math.PI / 2, 0, 0);
        tempQuaternion.setFromEuler(tempEuler);
        tempScale.setScalar(0);
        tempMatrix.compose(tempPosition, tempQuaternion, tempScale);
        ringInstanceRef.current.setMatrixAt(i, tempMatrix);
      });
      ringInstanceRef.current.instanceMatrix.needsUpdate = true;
    }

    // Initialize streaks
    if (streakInstanceRef.current) {
      energyStreaks.forEach((streak, i) => {
        tempPosition.set(0, streak.y, 0.45);
        tempScale.setScalar(0);
        tempMatrix.compose(tempPosition, tempQuaternion, tempScale);
        streakInstanceRef.current.setMatrixAt(i, tempMatrix);
      });
      streakInstanceRef.current.instanceMatrix.needsUpdate = true;
    }
  }, [rowData, crystalShards, glowParticles, energyStreaks]);

  // Single consolidated useFrame for ALL animations
  useFrame((state) => {
    if (startTime.current === null) {
      startTime.current = state.clock.elapsedTime;
    }

    const elapsed = state.clock.elapsedTime - startTime.current;
    const duration = 0.7;

    if (elapsed > duration) {
      onComplete?.();
      return;
    }

    // Animate energy sweeps (beams)
    if (beamInstanceRef.current && beamGlowInstanceRef.current) {
      rowData.forEach((data, i) => {
        const adjustedTime = elapsed - data.sweepDelay;

        if (adjustedTime <= 0 || adjustedTime > 0.35) {
          tempScale.setScalar(0);
          tempMatrix.compose(tempPosition, tempQuaternion, tempScale);
          beamInstanceRef.current.setMatrixAt(i, tempMatrix);
          beamGlowInstanceRef.current.setMatrixAt(i, tempMatrix);
          return;
        }

        const progress = adjustedTime / 0.35;
        const startX = data.sweepDirection > 0 ? -7 : 7;
        const endX = data.sweepDirection > 0 ? 7 : -7;
        const x = startX + (endX - startX) * progress;
        const intensity = Math.sin(progress * Math.PI);

        // Beam
        tempPosition.set(x, data.y, 0.5);
        tempScale.set(1.5 + intensity * 2, 1, 1);
        tempQuaternion.identity();
        tempMatrix.compose(tempPosition, tempQuaternion, tempScale);
        beamInstanceRef.current.setMatrixAt(i, tempMatrix);

        // Glow
        tempPosition.set(x, data.y, 0.4);
        tempScale.set(3 + intensity * 2, 1.8, 1);
        tempMatrix.compose(tempPosition, tempQuaternion, tempScale);
        beamGlowInstanceRef.current.setMatrixAt(i, tempMatrix);
      });
      beamInstanceRef.current.instanceMatrix.needsUpdate = true;
      beamGlowInstanceRef.current.instanceMatrix.needsUpdate = true;
    }

    // Animate row flashes
    if (flashInstanceRef.current && flashCoreInstanceRef.current) {
      rowData.forEach((data, i) => {
        const adjustedTime = elapsed - data.flashDelay;

        if (adjustedTime <= 0 || adjustedTime > 0.5) {
          tempScale.set(1, 0, 1);
          tempMatrix.compose(tempPosition, tempQuaternion, tempScale);
          flashInstanceRef.current.setMatrixAt(i, tempMatrix);
          flashCoreInstanceRef.current.setMatrixAt(i, tempMatrix);
          return;
        }

        const progress = adjustedTime / 0.5;
        const flashIntensity = progress < 0.3
          ? Math.sin((progress / 0.3) * Math.PI * 0.5)
          : Math.cos(((progress - 0.3) / 0.7) * Math.PI * 0.5);

        tempPosition.set(0, data.y, 0.3);
        tempScale.set(1, 1 + flashIntensity * 0.3, 1);
        tempQuaternion.identity();
        tempMatrix.compose(tempPosition, tempQuaternion, tempScale);
        flashInstanceRef.current.setMatrixAt(i, tempMatrix);

        tempPosition.set(0, data.y, 0.35);
        tempScale.set(1, 0.5 + flashIntensity * 0.3, 1);
        tempMatrix.compose(tempPosition, tempQuaternion, tempScale);
        flashCoreInstanceRef.current.setMatrixAt(i, tempMatrix);
      });
      flashInstanceRef.current.instanceMatrix.needsUpdate = true;
      flashCoreInstanceRef.current.instanceMatrix.needsUpdate = true;
    }

    // Animate crystal shards
    if (shardInstanceRef.current) {
      const gravity = -15;
      crystalShards.forEach((shard, i) => {
        const adjustedTime = elapsed - shard.delay;

        if (adjustedTime <= 0 || adjustedTime > 0.8) {
          tempScale.setScalar(0);
          tempMatrix.compose(tempPosition, tempQuaternion, tempScale);
          shardInstanceRef.current.setMatrixAt(i, tempMatrix);
          if (shardTrailInstanceRef.current) {
            shardTrailInstanceRef.current.setMatrixAt(i, tempMatrix);
          }
          return;
        }

        const t = adjustedTime;
        const x = shard.startPosition[0] + shard.velocity[0] * t;
        const y = shard.startPosition[1] + shard.velocity[1] * t + 0.5 * gravity * t * t;
        const z = shard.startPosition[2] + shard.velocity[2] * t;

        shard.rotation[0] += shard.rotationSpeed[0] * 0.016;
        shard.rotation[1] += shard.rotationSpeed[1] * 0.016;
        shard.rotation[2] += shard.rotationSpeed[2] * 0.016;

        tempPosition.set(x, y, z);
        tempEuler.set(shard.rotation[0], shard.rotation[1], shard.rotation[2]);
        tempQuaternion.setFromEuler(tempEuler);
        tempScale.setScalar(shard.size);
        tempMatrix.compose(tempPosition, tempQuaternion, tempScale);
        shardInstanceRef.current.setMatrixAt(i, tempMatrix);

        // Trail
        if (shardTrailInstanceRef.current && adjustedTime < 0.5) {
          const fadeProgress = adjustedTime / 0.8;
          tempPosition.set(x - shard.velocity[0] * 0.02, y - shard.velocity[1] * 0.02, z - 0.1);
          tempScale.set(shard.size * 0.5, shard.size * 3 * (1 - fadeProgress), 1);
          tempQuaternion.identity();
          tempMatrix.compose(tempPosition, tempQuaternion, tempScale);
          shardTrailInstanceRef.current.setMatrixAt(i, tempMatrix);
        } else if (shardTrailInstanceRef.current) {
          tempScale.setScalar(0);
          tempMatrix.compose(tempPosition, tempQuaternion, tempScale);
          shardTrailInstanceRef.current.setMatrixAt(i, tempMatrix);
        }
      });
      shardInstanceRef.current.instanceMatrix.needsUpdate = true;
      if (shardTrailInstanceRef.current) {
        shardTrailInstanceRef.current.instanceMatrix.needsUpdate = true;
      }
    }

    // Animate glow particles
    if (particleInstanceRef.current) {
      glowParticles.forEach((particle, i) => {
        const adjustedTime = elapsed - particle.delay;

        if (adjustedTime <= 0 || adjustedTime > 0.7) {
          tempScale.setScalar(0);
          tempMatrix.compose(tempPosition, tempQuaternion, tempScale);
          particleInstanceRef.current.setMatrixAt(i, tempMatrix);
          return;
        }

        const t = adjustedTime;
        const progress = adjustedTime / 0.7;
        const x = particle.startPosition[0] + Math.sin(t * 4 + particle.drift) * 0.3;
        const y = particle.startPosition[1] + t * particle.speed;
        const z = particle.startPosition[2] + t * 0.5;

        const pulse = Math.sin(t * 15) * 0.3 + 0.7;
        const fade = 1 - progress;
        const scale = pulse * fade * 0.15 + 0.05;

        tempPosition.set(x, y, z);
        tempScale.setScalar(scale);
        tempQuaternion.identity();
        tempMatrix.compose(tempPosition, tempQuaternion, tempScale);
        particleInstanceRef.current.setMatrixAt(i, tempMatrix);
      });
      particleInstanceRef.current.instanceMatrix.needsUpdate = true;
    }

    // Animate rings
    if (ringInstanceRef.current) {
      rowData.forEach((data, i) => {
        const adjustedTime = elapsed - data.ringDelay;

        if (adjustedTime <= 0 || adjustedTime > 0.4) {
          tempScale.setScalar(0);
          tempMatrix.compose(tempPosition, tempQuaternion, tempScale);
          ringInstanceRef.current.setMatrixAt(i, tempMatrix);
          return;
        }

        const progress = adjustedTime / 0.4;
        const scale = 1 + progress * 8;

        tempPosition.set(0, data.y, 0.2);
        tempEuler.set(Math.PI / 2, 0, 0);
        tempQuaternion.setFromEuler(tempEuler);
        tempScale.set(scale, 1, scale);
        tempMatrix.compose(tempPosition, tempQuaternion, tempScale);
        ringInstanceRef.current.setMatrixAt(i, tempMatrix);
      });
      ringInstanceRef.current.instanceMatrix.needsUpdate = true;
      ringInstanceRef.current.material.opacity = Math.max(0, 0.7 * (1 - elapsed / 0.4));
    }

    // Animate energy streaks
    if (streakInstanceRef.current) {
      energyStreaks.forEach((streak, i) => {
        const adjustedTime = elapsed - streak.delay;

        if (adjustedTime <= 0 || adjustedTime > 0.25) {
          tempScale.setScalar(0);
          tempMatrix.compose(tempPosition, tempQuaternion, tempScale);
          streakInstanceRef.current.setMatrixAt(i, tempMatrix);
          return;
        }

        const progress = adjustedTime / 0.25;
        const x = streak.direction * (-6 + progress * 12);
        const scaleX = 2 + Math.sin(progress * Math.PI) * 3;

        tempPosition.set(x, streak.y, 0.45);
        tempScale.set(scaleX, 1, 1);
        tempQuaternion.identity();
        tempMatrix.compose(tempPosition, tempQuaternion, tempScale);
        streakInstanceRef.current.setMatrixAt(i, tempMatrix);
      });
      streakInstanceRef.current.instanceMatrix.needsUpdate = true;
    }
  });

  return (
    <group ref={groupRef}>
      {/* Energy sweep beams - Instanced */}
      {rowData.length > 0 && (
        <>
          <instancedMesh
            ref={beamInstanceRef}
            args={[sharedBeamGeometry, beamMaterial, rowData.length]}
            frustumCulled={false}
          />
          <instancedMesh
            ref={beamGlowInstanceRef}
            args={[sharedGlowGeometry, beamGlowMaterial, rowData.length]}
            frustumCulled={false}
          />
        </>
      )}

      {/* Row flashes - Instanced */}
      {rowData.length > 0 && (
        <>
          <instancedMesh
            ref={flashInstanceRef}
            args={[sharedFlashGeometry, flashMaterial, rowData.length]}
            frustumCulled={false}
          />
          <instancedMesh
            ref={flashCoreInstanceRef}
            args={[sharedCoreGeometry, flashCoreMaterial, rowData.length]}
            frustumCulled={false}
          />
        </>
      )}

      {/* Crystal shards - Instanced */}
      {crystalShards.length > 0 && (
        <>
          <instancedMesh
            ref={shardInstanceRef}
            args={[sharedOctaGeometry, shardMaterial, crystalShards.length]}
            frustumCulled={false}
          />
          <instancedMesh
            ref={shardTrailInstanceRef}
            args={[sharedTrailGeometry, trailMaterial, crystalShards.length]}
            frustumCulled={false}
          />
        </>
      )}

      {/* Glow particles - Instanced */}
      {glowParticles.length > 0 && (
        <instancedMesh
          ref={particleInstanceRef}
          args={[sharedSphereGeometry, particleMaterial, glowParticles.length]}
          frustumCulled={false}
        />
      )}

      {/* Ring waves - Instanced */}
      {rowData.length > 0 && (
        <instancedMesh
          ref={ringInstanceRef}
          args={[sharedRingGeometry, ringMaterial, rowData.length]}
          frustumCulled={false}
        />
      )}

      {/* Energy streaks - Instanced */}
      {energyStreaks.length > 0 && (
        <instancedMesh
          ref={streakInstanceRef}
          args={[sharedStreakGeometry, streakMaterial, energyStreaks.length]}
          frustumCulled={false}
        />
      )}
    </group>
  );
};

export default IceLineClearEffect;
